import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { graphql } from '@apollo/client/react/hoc';
import get from 'lodash/get';
import compose from 'lodash/flowRight';
import { makeStyles } from '@material-ui/core/styles';
import green from '@material-ui/core/colors/green';
import lightBlue from '@material-ui/core/colors/lightBlue';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import consumeAppContext from '../../consumers/app';
import { updateEventStatusMutation, eventQuery } from '../../apollo/events';
import JoinToggleMessage from './join-toggle-message';

const useStyles = makeStyles(() => ({
  goingSelected: {
    backgroundColor: `${green[100]} !important`,
    '& > .MuiToggleButton-label': {
      color: green[900]
    }
  },
  interestedSelected: {
    backgroundColor: `${lightBlue[50]} !important`,
    '& > .MuiToggleButton-label': {
      color: lightBlue[900]
    }
  }
}));

const EventJoinToggle = (props) => {
  const {
    event,
    updateEventStatus,
    isLoggedIn,
    autoRsvp,
    onJoin,
    onRsvp,
    eventInvite
  } = props;
  const classes = useStyles();
  const [submitting, setSubmitting] = useState(false);
  const [autoRsvped, setAutoJoined] = useState(false);
  const status = get(event.membership, 'status');
  const disabled = !isLoggedIn || submitting || event.status !== 'PUBLISHED' || !event.permissions.joinable;

  useEffect(() => {
    if (autoRsvp && !autoRsvped && !disabled) {
      setSubmitting(true);
      updateEventStatus({
        variables: {
          eventId: event.id,
          status: autoRsvp,
          inviteToken: get(eventInvite, 'id')
        }
      })
        .then(() => {
          setAutoJoined(true);
          setSubmitting(false);
          if (onRsvp) {
            onRsvp();
          }
        })
        .catch(() => {
          alert('RSVP Failed');
          setAutoJoined(true);
          setSubmitting(false);
        });
    }
  }, ['isLoggedIn']);

  return (
    <div>
      <ToggleButtonGroup
        value={status}
        onChange={(e, newStatus) => {
          // TODO: alert if login expired

          setSubmitting(true);
          updateEventStatus({
            variables: {
              eventId: event.id,
              status: newStatus,
              inviteToken: get(eventInvite, 'id')
            }
          })
            .then(() => {
              setSubmitting(false);
              if (onRsvp) {
                onRsvp();
              }
            })
            .catch(() => {
              alert('RSVP Failed');
              setSubmitting(false);
            });
        }}
        exclusive
      >
        <ToggleButton
          variant="contained"
          disabled={disabled}
          key={1}
          value="GOING"
          classes={{
            selected: classes.goingSelected
          }}
        >
          Going
        </ToggleButton>
        <ToggleButton
          variant="contained"
          disabled={disabled}
          key={2}
          value="INTERESTED"
          classes={{
            selected: classes.interestedSelected
          }}
        >
          Interested
        </ToggleButton>
        <ToggleButton
          variant="contained"
          disabled={disabled}
          key={3}
          value="NOT_GOING"
          classes={{
            selected: classes.notGoingSelected
          }}
        >
          Not Going
        </ToggleButton>
      </ToggleButtonGroup>

      <JoinToggleMessage
        event={event}
        eventInvite={eventInvite}
        autoRsvp={autoRsvp}
        onJoin={onJoin}
      />
    </div>
  );
};

EventJoinToggle.propTypes = {
  event: PropTypes.object,
  eventInvite: PropTypes.object,
  updateEventStatus: PropTypes.func,
  isLoggedIn: PropTypes.bool,
  onJoin: PropTypes.func,
  autoRsvp: PropTypes.string,
  onRsvp: PropTypes.func
};

export default compose(
  consumeAppContext,
  graphql(updateEventStatusMutation, {
    name: 'updateEventStatus',
    options: ({ event, eventInvite }) => ({
      update: (proxy, { data: { updateEventStatus: membership } }) => {
        // update event query w/ updated membership object
        try {
          const eventQueryVariables = eventInvite ? {
            id: event.id,
            inviteToken: eventInvite.id
          } : {
            id: event.id
          };
          const data = proxy.readQuery({
            query: eventQuery,
            variables: eventQueryVariables,
          });
          proxy.writeQuery({
            query: eventQuery,
            variables: eventQueryVariables,
            data: {
              ...data,
              event: {
                ...data.event,
                membership
              }
            }
          });
        } catch (e) {
          // nothing
        }
      }
    })
  })
)(EventJoinToggle);